/**
 * Smart Link Utilities
 * Helper functions for parsing and detecting link types in CTA buttons.
 * Used by SmartButton to route links to the correct handler (form modal, anchor scroll, external tab).
 */

/**
 * Canonical in-app form-link grammar, `/{eventId}/form/{formId}`. The `g` flag lets callers
 * scan free text (e.g. layout/rich-text) for every occurrence; clone or reset `lastIndex`
 * before reuse if you call `.exec()`/`.test()` directly (`String.matchAll` handles it safely).
 */
export const FORM_LINK_PATTERN = /\/(\d+)\/form\/(\d+)/g;

/**
 * Parses a link to detect if it's a form link
 * @param href - The link to parse
 * @param eventId - The current event ID (optional, for stricter matching)
 * @returns Object with isFormLink boolean, formId, and the `?rt=` respondent-type
 *   invite token if present — a form-linked pricing card opens FormModal in-place
 *   without navigating, so this is the only way that per-tier token reaches it.
 */
export function parseFormLink(
  href: string,
  eventId?: number
): { isFormLink: boolean; formId: number | null; inviteToken: string | null } {
  if (!href || typeof href !== "string") {
    return { isFormLink: false, formId: null, inviteToken: null };
  }

  // Pattern: /{eventId}/form/{formId}. Non-global local copy so this single-match path
  // never shares lastIndex with the exported global pattern.
  const formLinkPattern = /\/(\d+)\/form\/(\d+)/;
  const match = href.match(formLinkPattern);

  if (!match) {
    return { isFormLink: false, formId: null, inviteToken: null };
  }

  const linkEventId = parseInt(match[1], 10);
  const formId = parseInt(match[2], 10);

  // If eventId provided, verify it matches
  if (eventId !== undefined && linkEventId !== eventId) {
    return { isFormLink: false, formId: null, inviteToken: null };
  }

  const rtMatch = href.match(/[?&]rt=([^&]+)/);
  const inviteToken = rtMatch ? decodeURIComponent(rtMatch[1]) : null;

  return { isFormLink: true, formId, inviteToken };
}

/**
 * Parses a link to detect if it's a hotel-booking link. Grammar: `/{eventId}/hotel-booking/{bookingLinkId}`
 * — the same relative path `buildHotelBookingPath()` builds and the standalone route serves. Mirrors
 * v1's `parseHotelBookingLink`, but (unlike v1's own call site) always applies the `eventId` scoping
 * check below when a caller provides one.
 * @param href - The link to parse
 * @param eventId - The current event ID (optional, for stricter matching)
 * @returns Object with isHotelBookingLink boolean and bookingLinkId
 */
export function parseHotelBookingLink(
  href: string,
  eventId?: number
): { isHotelBookingLink: boolean; bookingLinkId: number | null } {
  if (!href || typeof href !== "string") {
    return { isHotelBookingLink: false, bookingLinkId: null };
  }

  // Non-global local copy so this single-match path never shares lastIndex with the exported
  // global pattern.
  const hotelBookingLinkPattern = /\/(\d+)\/hotel-booking\/(\d+)/;
  const match = href.match(hotelBookingLinkPattern);

  if (!match) {
    return { isHotelBookingLink: false, bookingLinkId: null };
  }

  const linkEventId = parseInt(match[1], 10);
  const bookingLinkId = parseInt(match[2], 10);

  if (eventId !== undefined && linkEventId !== eventId) {
    return { isHotelBookingLink: false, bookingLinkId: null };
  }

  return { isHotelBookingLink: true, bookingLinkId };
}

/**
 * Checks if a link is an anchor link (starts with #)
 * @param href - The link to check
 * @returns Whether the link is an anchor link
 */
export function isAnchorLink(href: string): boolean {
  return typeof href === "string" && href.startsWith("#");
}

/**
 * Checks if a link is an external link (starts with http:// or https://)
 * @param href - The link to check
 * @returns Whether the link is an external link
 */
export function isExternalLink(href: string): boolean {
  if (!href || typeof href !== "string") return false;
  return href.startsWith("http://") || href.startsWith("https://");
}

/**
 * Checks whether a link uses a dangerous URL scheme that must never be rendered into an
 * `href`/navigated to (would execute attacker script). Stored landing-page links are
 * organizer free-text, so the public renderer must reject these regardless of the editor.
 * Leading whitespace/control chars are stripped first since browsers ignore them when
 * resolving the scheme (e.g. `"  javascript:..."` still runs).
 * @param href - The link to check
 * @returns Whether the link uses a javascript:/data:/vbscript:/file: scheme
 */
export function isUnsafeLinkScheme(href: string): boolean {
  if (!href || typeof href !== "string") return false;
  // eslint-disable-next-line no-control-regex
  const stripped = href.replace(/[\u0000-\u0020]+/g, "");
  return /^(javascript|data|vbscript|file):/i.test(stripped);
}

/**
 * Prefix of the magic "email me this template" link, `#__email-{templateId}`, stored in a CTA
 * button's existing `url`. It starts with `#` so any surface that doesn't know about it degrades to
 * a harmless in-page anchor instead of navigating away. Real section anchors come from
 * `generateAnchorId`, which never emits `_`, so they can't collide with it.
 */
export const EMAIL_LINK_PREFIX = "#__email-";

/** Exact grammar for a well-formed email link. Non-global, so it never carries `lastIndex`. */
const EMAIL_LINK_PATTERN = /^#__email-(\d+)$/;

/** Component types whose CTA buttons can send an email. Email links anywhere else render inert. */
const EMAIL_BUTTON_COMPONENT_TYPES: readonly string[] = ["navbar", "hero", "textButton"];

/**
 * Builds the email link for a template, e.g. `#__email-12`.
 * @param templateId - The email template id to send when the button is clicked
 * @returns The link to store in a CTA button's `url`
 */
export function buildEmailLink(templateId: number): string {
  return `${EMAIL_LINK_PREFIX}${templateId}`;
}

/**
 * Strictly parses an email link. Only the exact `#__email-{digits}` shape counts (no padding,
 * suffix, or case change), and the id must be a positive safe integer. Anything else is not an
 * email link, so a malformed `#__email-...` value falls back to a harmless anchor in the renderers.
 * @param href - The link to parse
 * @returns The template id, or null when `href` is not a well-formed email link
 */
export function parseEmailLink(href: string | null | undefined): number | null {
  if (!href || typeof href !== "string") return null;

  const match = EMAIL_LINK_PATTERN.exec(href);
  if (!match) return null;

  const templateId = Number(match[1]);
  if (!Number.isSafeInteger(templateId) || templateId <= 0) return null;
  return templateId;
}

/**
 * Whether a link is a well-formed email link (see {@link parseEmailLink}).
 * @param href - The link to check
 * @returns true when `href` parses to a template id
 */
export function isEmailLink(href: string | null | undefined): boolean {
  return parseEmailLink(href) !== null;
}

/**
 * Loose check for anything that looks like an email link, malformed ones included. Agent surfaces
 * (copilot writes, chatbot context) use this so no `#__email-...` value is ever written by the model
 * or echoed to a guest, even one that wouldn't parse.
 * @param href - The link to check
 * @returns true when `href`, ignoring leading whitespace, starts with {@link EMAIL_LINK_PREFIX}
 */
export function hasEmailLinkPrefix(href: string | null | undefined): boolean {
  if (!href || typeof href !== "string") return false;
  return href.trimStart().startsWith(EMAIL_LINK_PREFIX);
}

/**
 * Returns the link strings on a component's send-capable buttons: `ctaButtons[].url`, or the legacy
 * navbar `ctaLink` when `ctaButtons` is absent (mirroring `getNavbarCtaButtons`). Empty for any
 * other component type or shape.
 * @param component - One layout-tree entry, untyped so this file stays import-free
 * @returns The candidate link strings
 */
function getEmailButtonUrls(component: unknown): string[] {
  if (!component || typeof component !== "object") return [];

  const { type, ctaButtons, ctaText, ctaLink } = component as Record<string, unknown>;
  if (typeof type !== "string" || !EMAIL_BUTTON_COMPONENT_TYPES.includes(type)) return [];

  if (Array.isArray(ctaButtons)) {
    return ctaButtons
      .map((button: unknown) =>
        button && typeof button === "object" ? (button as Record<string, unknown>).url : undefined
      )
      .filter((url): url is string => typeof url === "string");
  }

  if (type === "navbar" && ctaText && typeof ctaLink === "string") return [ctaLink];
  return [];
}

/**
 * Collects the email template ids referenced by a page's layout tree, deduped in first-seen order.
 * Only send-capable buttons are read (navbar, hero, and textButton CTA buttons); email links on
 * other surfaces are inert and ignored. Visibility toggles are not consulted.
 * @param layoutTree - A landing page's layout tree (array of page components); null/undefined allowed
 * @returns The referenced template ids
 */
export function collectEmailTemplateIds(layoutTree: readonly unknown[] | null | undefined): number[] {
  if (!Array.isArray(layoutTree)) return [];

  const ids = new Set<number>();
  for (const component of layoutTree) {
    for (const url of getEmailButtonUrls(component)) {
      const templateId = parseEmailLink(url);
      if (templateId !== null) ids.add(templateId);
    }
  }
  return [...ids];
}
